import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Star, TrendingUp, Users, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  elite:    { color: 'text-amber-300',   bg: 'bg-gradient-to-br from-amber-500/25 to-yellow-500/10',   bd: 'border-amber-400/60',   emoji: '🏆', label: 'Elite'   },
  trusted:  { color: 'text-blue-300',    bg: 'bg-gradient-to-br from-blue-500/25 to-cyan-500/10',      bd: 'border-blue-400/60',    emoji: '💎', label: 'Trusted' },
  verified: { color: 'text-emerald-300', bg: 'bg-gradient-to-br from-emerald-500/25 to-green-500/10',  bd: 'border-emerald-400/60', emoji: '✅', label: 'Verified' },
  unverified:{ color: 'text-slate-300',   bg: 'bg-slate-500/15',   bd: 'border-slate-400/30', emoji: '🔓', label: 'Unverified' },
};

const Stars = ({ score }) => {
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return (
    <span className="inline-flex">
      {[0,1,2,3,4].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i < full ? 'fill-current' : i === full && half ? 'fill-current opacity-60' : 'opacity-25'}`} />
      ))}
    </span>
  );
};

/**
 * Embeddable Anant widget. Two modes:
 *   ?shop=TOKEN  → shows a specific shop's Trust Badge (business_name + tier + score + count)
 *   (default)    → shows network-wide Trust Distribution (total shops, tier counts)
 *
 * Optional params:
 *   ?theme=dark|light  (default: dark)
 */
export default function NetworkLiveWidget() {
  const [params] = useSearchParams();
  const shopToken = params.get('shop') || '';
  const theme = params.get('theme') || 'dark';
  const [shop, setShop] = useState(null);
  const [net, setNet] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      if (shopToken) {
        const r = await axios.get(`${API}/vyapar/magic-qr/public/${shopToken}`);
        setShop(r.data);
        // fire-and-forget view tracker
        axios.post(`${API}/safety-qr/widget-view`, { shop_token: shopToken, referrer: document.referrer || '' }).catch(() => {});
      } else {
        const r = await axios.get(`${API}/safety-qr/network-live`);
        setNet(r.data);
        axios.post(`${API}/safety-qr/widget-view`, { shop_token: null, referrer: document.referrer || '' }).catch(() => {});
      }
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
  }, [shopToken]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const isDark = theme === 'dark';
  const bg = isDark ? 'bg-gradient-to-br from-slate-950 to-purple-950 text-white' : 'bg-white text-slate-900';
  const subText = isDark ? 'text-slate-400' : 'text-slate-500';
  const cta = 'https://dvc-shop-build.emergent.host/anant-live';

  if (err) return <div className={`min-h-[220px] flex items-center justify-center ${bg}`}><div className="text-xs opacity-70 p-3 text-center">{err}</div></div>;

  // =============== SHOP MODE ===============
  if (shopToken) {
    if (!shop) return <div className={`min-h-[220px] flex items-center justify-center ${bg}`}><div className="text-xs opacity-70 animate-pulse">Loading Anant badge…</div></div>;
    const meta = TIER_META[shop.tier] || TIER_META.unverified;
    return (
      <div className={`min-h-[220px] p-4 ${bg}`} data-testid="widget-shop-mode">
        <div className={`rounded-2xl border ${meta.bd} ${meta.bg} p-4 backdrop-blur-sm`}>
          <div className="flex items-center gap-1 mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-400">LIVE · Anant Network</span>
          </div>
          <div className="flex items-start gap-3">
            <div className={`w-14 h-14 rounded-xl ${meta.bg} border ${meta.bd} flex items-center justify-center text-3xl shrink-0`}>{meta.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-lg leading-tight flex items-center gap-1 truncate" data-testid="widget-shop-name">
                {shop.business_name}
                <ShieldCheck className={`w-4 h-4 ${meta.color} shrink-0`} />
              </div>
              <div className={`text-xs ${subText} truncate`}>{shop.industry || 'Local Business'}</div>
              <div className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border ${meta.bd} text-[10px] font-bold ${meta.color}`}>
                {meta.label} by Anant Network
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2">
            <div>
              <div className={`text-[9px] uppercase font-bold ${subText}`}>Trust Score</div>
              <div className={`text-2xl font-extrabold ${meta.color} flex items-center gap-1`}>
                {(shop.trust_score || 0).toFixed(1)}
                <span className={meta.color}><Stars score={shop.trust_score || 0} /></span>
              </div>
            </div>
            <div>
              <div className={`text-[9px] uppercase font-bold ${subText}`}>Trusted Customers</div>
              <div className="text-2xl font-extrabold flex items-center gap-1">
                <Users className={`w-4 h-4 ${meta.color}`} /> {shop.customer_count}
              </div>
            </div>
          </div>
          <a href={cta} target="_blank" rel="noreferrer" className={`block text-center mt-3 text-[10px] font-bold ${meta.color} hover:underline`}>
            Verify on anantwebportal.com → 🚀
          </a>
        </div>
      </div>
    );
  }

  // =============== NETWORK MODE ===============
  if (!net) return <div className={`min-h-[220px] flex items-center justify-center ${bg}`}><div className="text-xs opacity-70 animate-pulse">Loading Anant Network…</div></div>;
  const { tiers, totals } = net;
  return (
    <div className={`min-h-[220px] p-4 ${bg}`} data-testid="widget-network-mode">
      <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-400">LIVE · Anant Network</span>
          </div>
          <TrendingUp className="w-3.5 h-3.5 text-fuchsia-300" />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg border border-white/10 p-2">
            <div className="text-[9px] uppercase font-bold text-fuchsia-300 flex items-center gap-1"><Users className="w-3 h-3" /> Shops</div>
            <div className="text-2xl font-extrabold">{totals.shops}</div>
          </div>
          <div className="rounded-lg border border-white/10 p-2">
            <div className="text-[9px] uppercase font-bold text-red-300 flex items-center gap-1"><Shield className="w-3 h-3" /> Safety QRs</div>
            <div className="text-2xl font-extrabold">{totals.safety_qrs}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-center">
          {['elite', 'trusted', 'verified'].map((t) => {
            const m = TIER_META[t];
            return (
              <div key={t} className={`rounded-lg border ${m.bd} ${m.bg} py-2`} data-testid={`widget-tier-${t}`}>
                <div className="text-lg">{m.emoji}</div>
                <div className={`text-xl font-extrabold ${m.color}`}>{tiers[t] || 0}</div>
                <div className={`text-[8px] uppercase ${subText}`}>{m.label}</div>
              </div>
            );
          })}
        </div>
        <a href={cta} target="_blank" rel="noreferrer" className="block text-center mt-3 text-[10px] font-bold text-fuchsia-300 hover:underline">
          Powered by Anant Network 🚀 anantwebportal.com
        </a>
      </div>
    </div>
  );
}
